import { z } from 'zod';

export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 128;

/** Header the native app sends so the API returns a bearer token instead of setting a cookie. */
export const CLIENT_PLATFORM_HEADER = 'x-client-platform';
export const clientPlatformSchema = z.enum(['web', 'native']);
export type ClientPlatform = z.infer<typeof clientPlatformSchema>;

// Messages are user-facing (Italian): the API returns them and the app shows them as they are.
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.email('Inserisci un indirizzo email valido.').max(254, "L'email è troppo lunga."));

export const newPasswordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `La password deve avere almeno ${PASSWORD_MIN_LENGTH} caratteri.`)
  .max(PASSWORD_MAX_LENGTH, 'La password è troppo lunga.');

const passwordSchema = z
  .string()
  .min(1, 'Inserisci la password.')
  .max(PASSWORD_MAX_LENGTH, 'La password è troppo lunga.');

const tokenSchema = z.string().min(1).max(256);

const displayNameSchema = z
  .string()
  .trim()
  .min(1, 'Inserisci il tuo nome.')
  .max(60, 'Il nome è troppo lungo.');

export const registerInputSchema = z.object({
  email: emailSchema,
  password: newPasswordSchema,
  displayName: displayNameSchema,
});
export type RegisterInput = z.infer<typeof registerInputSchema>;

export const loginInputSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
});
export type LoginInput = z.infer<typeof loginInputSchema>;

export const verifyEmailInputSchema = z.object({ token: tokenSchema });
export type VerifyEmailInput = z.infer<typeof verifyEmailInputSchema>;

export const forgotPasswordInputSchema = z.object({ email: emailSchema });
export type ForgotPasswordInput = z.infer<typeof forgotPasswordInputSchema>;

export const resetPasswordInputSchema = z.object({
  token: tokenSchema,
  password: newPasswordSchema,
});
export type ResetPasswordInput = z.infer<typeof resetPasswordInputSchema>;

export const updateProfileInputSchema = z.object({ displayName: displayNameSchema });
export type UpdateProfileInput = z.infer<typeof updateProfileInputSchema>;

/** Accounts created with Google or Apple have no password: their session is the confirmation. */
export const deleteAccountInputSchema = z.object({ password: passwordSchema.optional() });
export type DeleteAccountInput = z.infer<typeof deleteAccountInputSchema>;

/** Emptying an account: the word typed on purpose, and the password when there is one. */
export const clearDataInputSchema = z.object({
  confirm: z
    .string()
    .refine((value) => value.trim().toUpperCase() === 'SVUOTA', 'Scrivi SVUOTA per confermare.'),
  password: passwordSchema.optional(),
});
export type ClearDataInput = z.infer<typeof clearDataInputSchema>;

export const oauthProviderSchema = z.enum(['google', 'apple']);
export type OAuthProvider = z.infer<typeof oauthProviderSchema>;

const APPLE_SIGN_IN_FAILED = 'Accesso con Apple non riuscito. Riprova.';

/** Sign in with Apple in the iOS app: the sheet is Apple's, the app sends what it gets back. */
export const appleNativeSignInSchema = z.object({
  identityToken: z.string(APPLE_SIGN_IN_FAILED).min(1, APPLE_SIGN_IN_FAILED).max(8192),
  /** The value whose SHA-256 went to Apple: only who started this sign-in knows it. */
  nonce: z.string(APPLE_SIGN_IN_FAILED).min(16, APPLE_SIGN_IN_FAILED).max(128),
  /** Apple tells the name only the first time, and only to the app. */
  name: z.string().trim().max(120).nullable().optional(),
});
export type AppleNativeSignIn = z.infer<typeof appleNativeSignInSchema>;

/** Where the system browser comes back to, when the phone app signs in with Google. */
export const NATIVE_AUTH_REDIRECT = 'dovefiniscono://auth';

/** The code the browser brought back, and the secret that only the app that started knows. */
export const nativeSignInExchangeSchema = z.object({
  code: z.string().min(1).max(200),
  verifier: z.string().min(32).max(200),
});
export type NativeSignInExchange = z.infer<typeof nativeSignInExchangeSchema>;

export const userSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  displayName: z.string(),
  emailVerified: z.boolean(),
  /** False for accounts created with Google or Apple that never set a password. */
  hasPassword: z.boolean().default(true),
  locale: z.string(),
  timezone: z.string(),
  createdAt: z.iso.datetime(),
});
export type User = z.infer<typeof userSchema>;

/** `token` is present only for native clients; web clients receive an HttpOnly cookie. */
export const authResponseSchema = z.object({
  user: userSchema,
  token: z.string().optional(),
});
export type AuthResponse = z.infer<typeof authResponseSchema>;

export const meResponseSchema = z.object({ user: userSchema });
export type MeResponse = z.infer<typeof meResponseSchema>;
